import React from "react";
import "./FindTripAsRiderScreen.css";
import { 
  IonPage, 
  IonContent, 
  IonButton, 
  IonIcon,
  IonCard,
  IonCardContent
} from '@ionic/react';
import { arrowBack, shield } from 'ionicons/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatTime, formatDate } from '../../utils/dateTimeUtils';

const FindTripAsRider = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, tripResults, selectedDate, originLocation, destinationLocation } = location.state;

  // Process trip results
  const validTrips = tripResults?.optimized_routes?.filter(route => route?.driverPostsTrip_id) || 
                     tripResults?.filter(trip => trip?.driver_name) || [];

  const calculateTotalCost = (route) => {
    if (!route.ordered_stops) return 'N/A';
    
    const pickupStop = route.ordered_stops.find(stop => stop.type === 'pickup');
    const dropoffStop = route.ordered_stops.find(stop => stop.type === 'dropoff');
    const costPerKM = route.costPerKM ?? 0.56;
    
    const hasDistances = Number.isFinite(pickupStop?.distanceMeters) && Number.isFinite(dropoffStop?.distanceMeters);
    const hasCost = Number.isFinite(costPerKM);

    if (hasDistances && hasCost) {
      const distanceKm = (dropoffStop.distanceMeters - pickupStop.distanceMeters) / 1000;
      return (distanceKm * costPerKM).toFixed(2);
    }
    return 'N/A';
  };

  const handleSelectTrip = (trip) => {
    navigate("/viewresultasrider", {
      state: { user, trip, originLocation, destinationLocation }
    });
  };

  const formatSelectedDate = () => {
    if (!selectedDate) return formatDate(new Date());
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const extractTripData = (route) => {
    const trip = route.transit || route;
    const isNewFormat = route.ordered_stops && route.transit;
    
    const driverName = trip.driver_name || route.driver_name || 'Driver';
    const calculatedPrice = calculateTotalCost(route);
    
    let locations = {};
    let times = {};
    
    if (isNewFormat && route.ordered_stops) {
      const stops = route.ordered_stops.reduce((acc, stop) => {
        acc[stop.type] = stop;
        return acc;
      }, {});
      
      locations = {
        start: tripResults?.startLocName || 'Start Location',
        end: tripResults?.endLocName || 'Destination',
        pickup: stops.pickup?.name || 'Pickup Location',
        dropoff: stops.dropoff?.name || 'Drop-off Location'
      };
      
      times = {
        start: stops.origin?.arrivalDateTime || trip.start_to_pickup?.departBy || tripResults?.arrivalDate,
        pickup: stops.pickup?.arrivalDateTime || tripResults?.arrivalDate,
        dropoff: stops.dropoff?.arrivalDateTime || tripResults?.arrivalDate,
        destination: stops.destination?.arrivalDateTime || trip.dropoff_to_destination?.arriveBy || tripResults?.arrivalDate
      };
    } else {
      locations = {
        start: trip.RiderStartLoc_name || trip.startLocName || 'Start Location',
        end: trip.RiderDestinationLoc_name || trip.endLocName || 'Destination',
        pickup: trip.RiderPickupLoc || trip.pickupLocation || locations.start,
        dropoff: trip.RiderDropoffLoc || trip.dropoffLocation || locations.end
      };
      
      times = {
        start: trip.startTime || trip.departureTime,
        pickup: trip.RiderPickupTime || trip.pickupTime,
        dropoff: trip.RiderDropoffTime || trip.dropoffTime,
        destination: trip.destinationTime || trip.arrivalTime
      };
    }
    
    return { driverName, calculatedPrice, locations, times };
  };

  return (
    <IonPage className="trip-planner-page">
      <IonContent fullscreen className="trip-planner-content">
        <div className="content-container">
          <div className="user-header">
            <IonButton fill="clear" className="back-button" onClick={() => navigate(-1)}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </div>

          <div className="page-title-section">
            <h2 className="page-title">Available trips</h2>
          </div>

          <div className="trips-container">
            <div className="date-display">{formatSelectedDate()}</div>

            {validTrips.length > 0 ? (
              validTrips.map((route, index) => {
                const { driverName, calculatedPrice, locations, times } = extractTripData(route);
                
                return (
                  <div key={index} className="trip-card" onClick={() => handleSelectTrip(route)}>
                    <div className="card-header">
                      <div className="driver-info">
                        <span className="driver-name">{driverName}</span>
                        <IonIcon icon={shield} className="shield-icon" />
                        <span className="driver-tier">Verified</span>
                      </div>
                      <span className="trip-price">${calculatedPrice}</span>
                    </div>

                    <div className="origin-stop">
                      <div className="origin-circle"></div>
                      <span className="origin-time">{times.start ? formatTime(times.start) : 'Time TBD'}</span>
                      <span className="origin-location">{locations.start}</span>
                    </div>
                    
                    <div className="vertical-line"></div>
                    
                    <div className="pickup-stop">
                      <div className="pickup-circle"></div>
                      <span className="pickup-time">{times.pickup ? formatTime(times.pickup) : 'Time TBD'}</span>
                      <span className="pickup-location">{locations.pickup}</span>
                    </div>
                    
                    <div className="vertical-line"></div>
                    
                    <div className="dropoff-stop">
                      <div className="dropoff-triangle"></div>
                      <span className="dropoff-time">{times.dropoff ? formatTime(times.dropoff) : 'Time TBD'}</span>
                      <span className="dropoff-location">{locations.dropoff}</span>
                    </div>
                    
                    <div className="vertical-line"></div>
                    
                    <div className="destination-stop">
                      <div className="destination-circle"></div>
                      <span className="destination-time">{times.destination ? formatTime(times.destination) : 'Time TBD'}</span>
                      <span className="destination-location">{locations.end}</span>
                    </div>

                    <div className="separator"></div>

                    <div className="card-footer">
                      <div className="details-btn">
                        Details
                        <IonIcon icon={arrowBack} className="arrow" />
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <IonCard className="no-trips-card">
                <IonCardContent>
                  <div className="no-trips-message">
                    <p>No trips found.</p>
                    <p>We are adding the functionality for passenger to post requests soon.</p>
                  </div>
                </IonCardContent>
              </IonCard>
            )}
            
            <div className="bottom-message">
              <p>Can't find a trip? Post or view requests.</p>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default FindTripAsRider;